import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, DIALOG_MODE_VIEW } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IBodyCreatePriceItem, IPriceItem } from '~/utils/interface/priceItem';
import { BaseSelect } from '~/components/common';
import { handleCreatePriceItem, handleEditPriceItem, handleGetPriceItems } from '~/thunks/price-item/priceItemsThunk';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_UNITS } from '~/utils/constants/master';
import { priceItemActions } from '~/thunks/price-item/priceItemsSlice';

type EditPriceItemDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: IPriceItem;
  disable?: boolean;
  children?: React.ReactNode;
  showFullField?: boolean;
};

const initialValues: IPriceItem = { isTax: true };

const EditPriceComponentDialog = (props: EditPriceItemDialogProps) => {
  const { t } = useTranslation(['admin/price-item']);
  const { mode = DIALOG_MODE_CREATE, data } = props;
  const dispatch = useAppDispatch();
  const { units } = useAppSelector((state) => state.master);
  const { selectedPrices } = useAppSelector((state) => state.price);
  const { selectedPriceComponents } = useAppSelector((state) => state.priceComponent);
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        const formattedValues: IBodyCreatePriceItem = {
          ...values,
          price: typeof values.price === 'object' ? values.price?.id : values.price,
          priceComponent: typeof values.priceComponent === 'object' ? values.priceComponent?.id : values.priceComponent,
        };

        if (mode === DIALOG_MODE_CREATE) {
          await dispatch(handleCreatePriceItem(formattedValues)).unwrap();
          dispatch(priceItemActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          await dispatch(handleGetPriceItems({ limit: 0 })).unwrap();
        } else if (mode === DIALOG_MODE_EDIT && data?.id) {
          await dispatch(handleEditPriceItem({ id: data.id, params: formattedValues })).unwrap();
          dispatch(priceItemActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
        }
        handleSetOpen(false, true);
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });

  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_UNITS],
      })
    );
  }, [dispatch]);
  useEffect(() => {
    // eslint-disable-next-line no-mixed-operators
    if ((mode === DIALOG_MODE_EDIT || mode === DIALOG_MODE_VIEW) && data) {
      form.setFieldValue('code', cast(data?.code));
      form.setFieldValue('color', cast(data?.color));
      form.setFieldValue('location', cast(data?.location));
      form.setFieldValue('title', cast(data?.title));
      form.setFieldValue('unit', cast(data?.unit));
      form.setFieldValue('unitPrice', cast(data?.unitPrice));
      form.setFieldValue('isTax', cast(data?.isTax));
      form.setFieldValue('manufacturer', cast(data?.manufacturer));
      form.setFieldValue('size', cast(data?.size));
      form.setFieldValue('productLine', cast(data?.productLine));
      form.setFieldValue('productType', cast(data?.productType));
      form.setFieldValue('thickness', cast(data?.thickness));
      form.setFieldValue('price', cast(data?.price?.id));
      form.setFieldValue('priceComponent', cast(data?.priceComponent?.id));
      form.setFieldValue('description', cast(data?.description));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
    form.resetForm();
  };

  const handleCancel = () => {
    handleSetOpen(false);
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE
          ? t('addNewPriceItem')
          : mode === DIALOG_MODE_VIEW
          ? t('priceListItem')
          : t('editPriceItem')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} className='mb-[20px]'>
          {/* Code Field */}
          <Grid item xs={12} sm={6}>
            <InputLabel>{t('properties.code')}</InputLabel>
            <TextField
              name='code'
              placeholder={t('properties.code')}
              value={form.values.code}
              onChange={form.handleChange}
              error={Boolean(form.errors.code)}
              helperText={form.errors.code}
              fullWidth
              disabled={props.disable}
            />
          </Grid>

          {/* Title Field */}
          <Grid item xs={12} sm={6}>
            <InputLabel required={true}>{t('properties.title')}</InputLabel>
            <TextField
              name='title'
              placeholder={t('properties.title')}
              value={form.values.title}
              onChange={form.handleChange}
              error={Boolean(form.errors.title)}
              helperText={form.errors.title}
              fullWidth
              disabled={props.disable}
            />
          </Grid>

          {/* Price List Select */}
          <Grid item xs={12} sm={6}>
            <InputLabel required={true}>{t('properties.priceList')}</InputLabel>
            <BaseSelect
              name='price'
              placeholder={t('properties.priceList')}
              value={form.values.price} // use the ID directly
              options={selectedPrices}
              onChange={form.handleChange}
              error={Boolean(form.errors.price)}
              helperText={form.errors.price}
              fullWidth
              disabled={props.disable}
            />
          </Grid>
          {/* Component Select */}
          <Grid item xs={12} sm={6}>
            <InputLabel required={true}>{t('properties.component')}</InputLabel>
            <BaseSelect
              name='priceComponent'
              placeholder={t('properties.component')}
              value={form.values.priceComponent}
              options={selectedPriceComponents}
              onChange={form.handleChange}
              error={Boolean(form.errors.priceComponent)}
              helperText={form.errors.priceComponent}
              fullWidth
              disabled={props.disable}
            />
          </Grid>

          {/* Unit Field */}

          <Grid item xs={12} sm={6}>
            <InputLabel required={true}>{t('properties.unit')}</InputLabel>
            <BaseSelect
              name='unit'
              placeholder={t('properties.unit')}
              value={form.values.unit}
              options={units}
              onChange={form.handleChange}
              error={Boolean(form.errors.unit)}
              helperText={form.errors.unit}
              fullWidth
              disabled={props.disable}
            />
          </Grid>

          {/* Unit Price Field */}
          <Grid item xs={12} sm={6}>
            <InputLabel required={true}>{t('properties.unitPrice')}</InputLabel>

            <NumericFormat
              customInput={TextField}
              name='unitPrice'
              placeholder={t('properties.unitPrice')}
              value={form.values.unitPrice}
              onValueChange={(val) => form.setFieldValue('unitPrice', val.floatValue)}
              thousandSeparator={true}
              prefix={'$'}
              error={Boolean(form.errors.unitPrice)}
              helperText={form.errors.unitPrice}
              fullWidth
              disabled={props.disable}
              decimalScale={2}
            />
          </Grid>

          {/* Taxable Radio Buttons */}

          {props.showFullField && (
            <>
              <Grid item xs={12} sm={6}>
                <InputLabel>{t('properties.taxable')}</InputLabel>
                <RadioGroup
                  name='isTax'
                  value={cast(form.values.isTax)} // Use cast function to convert boolean to string
                  onChange={(e) => form.setFieldValue('isTax', e.target.value)}
                  row
                >
                  <FormControlLabel value='true' control={<Radio disabled={props.disable} />} label={t('yes')} />
                  <FormControlLabel value='false' control={<Radio disabled={props.disable} />} label={t('no')} />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel>{t('properties.manufacturer')}</InputLabel>
                <TextField
                  name='manufacturer'
                  placeholder={t('properties.manufacturer')}
                  value={form.values.manufacturer}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.manufacturer)}
                  helperText={form.errors.manufacturer}
                  fullWidth
                  disabled={props.disable}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabel>{t('properties.size')}</InputLabel>
                <TextField
                  name='size'
                  placeholder={t('properties.size')}
                  value={form.values.size}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.size)}
                  helperText={form.errors.size}
                  fullWidth
                  disabled={props.disable}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabel>{t('properties.productLine')}</InputLabel>
                <TextField
                  name='productLine'
                  placeholder={t('properties.productLine')}
                  value={form.values.productLine}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.productLine)}
                  helperText={form.errors.productLine}
                  fullWidth
                  disabled={props.disable}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabel>{t('properties.productType')}</InputLabel>
                <TextField
                  name='productType'
                  placeholder={t('properties.productType')}
                  value={form.values.productType}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.productType)}
                  helperText={form.errors.productType}
                  fullWidth
                  disabled={props.disable}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabel>{t('properties.thickness')}</InputLabel>
                <TextField
                  name='thickness'
                  placeholder={t('properties.thickness')}
                  value={form.values.thickness}
                  onChange={form.handleChange}
                  error={Boolean(form.errors.thickness)}
                  helperText={form.errors.thickness}
                  fullWidth
                  disabled={props.disable}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={12}>
            <InputLabel>{t('properties.description')}</InputLabel>
            <TextField
              name='description'
              placeholder={t('properties.addYourDescription')}
              value={form.values.description}
              onChange={form.handleChange}
              error={Boolean(form.errors.description)}
              helperText={form.errors.description}
              fullWidth
              multiline
              disabled={props.disable}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {mode === DIALOG_MODE_VIEW ? (
          <>
            {' '}
            <Button color='secondary' variant='contained' onClick={handleCancel}>
              {t('close', { ns: 'admin/common' })}
            </Button>
          </>
        ) : (
          <>
            <Button color='secondary' variant='contained' onClick={handleCancel}>
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button
              color='primary'
              variant='contained'
              onClick={() => {
                form.handleSubmit();
                if (props.setOpenDrawer) {
                  props.setOpenDrawer(false);
                }
              }}
            >
              {t('save', { ns: 'admin/common' })}
            </Button>{' '}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditPriceComponentDialog);
